
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, X, Camera, Star, User, Image } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface FarmImage {
  id: string;
  url: string;
  role: 'profile_icon' | 'main_farm' | 'farm_extra';
}

interface FarmImagesUploadProps {
  currentImages?: FarmImage[];
  onImagesUpdate: (images: FarmImage[], profileIconUrl?: string, mainFarmUrl?: string) => void;
  isEditing: boolean;
}

export const FarmImagesUpload = ({ 
  currentImages = [], 
  onImagesUpdate, 
  isEditing 
}: FarmImagesUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<FarmImage[]>(currentImages);
  const { toast } = useToast();

  useEffect(() => {
    setImages(currentImages);
  }, [currentImages]);

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'profile_icon':
        return <User size={12} className="mr-1" />;
      case 'main_farm':
        return <Star size={12} className="mr-1" />;
      default:
        return <Camera size={12} className="mr-1" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'profile_icon':
        return 'Profile Icon';
      case 'main_farm':
        return 'Main Farm';
      default:
        return 'Farm Image';
    }
  };

  const getAvailableRoles = (): ('profile_icon' | 'main_farm' | 'farm_extra')[] => {
    const usedRoles = images.map(img => img.role);
    const allRoles: ('profile_icon' | 'main_farm' | 'farm_extra')[] = ['profile_icon', 'main_farm', 'farm_extra', 'farm_extra'];
    
    return allRoles.filter((role, index) => {
      if (role === 'farm_extra') {
        const extraCount = usedRoles.filter(r => r === 'farm_extra').length;
        return extraCount < 2;
      }
      return !usedRoles.includes(role);
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const availableRoles = getAvailableRoles();
    if (files.length > availableRoles.length) {
      toast({
        title: "Error",
        description: `You can only upload ${availableRoles.length} more image(s)`,
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      const uploadedImages: FarmImage[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast({
            title: "Error",
            description: `${file.name} is not an image file`,
            variant: "destructive",
          });
          continue;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast({
            title: "Error",
            description: `${file.name} is too large. File size must be less than 5MB`,
            variant: "destructive",
          });
          continue;
        }

        // Create a unique filename
        const fileExt = file.name.split('.').pop();
        const role = availableRoles[i];
        const fileName = `${user.id}/${role}-${Date.now()}-${Math.random()}.${fileExt}`;

        // Get proper MIME type for the image
        const mimeType = file.type || `image/${fileExt === 'jpg' ? 'jpeg' : fileExt}`;
        
        // Upload file to Supabase Storage
        const { data, error } = await supabase.storage
          .from('farm-logos')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: mimeType
          });

        if (error) throw error;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('farm-logos')
          .getPublicUrl(fileName);

        uploadedImages.push({
          id: fileName,
          url: publicUrl,
          role: role
        });
      }

      const newImages = [...images, ...uploadedImages];
      setImages(newImages);
      
      const profileIcon = newImages.find(img => img.role === 'profile_icon');
      const mainFarm = newImages.find(img => img.role === 'main_farm');
      onImagesUpdate(newImages, profileIcon?.url, mainFarm?.url);

      toast({
        title: "Success",
        description: `${uploadedImages.length} image(s) uploaded successfully!`,
      });

    } catch (error) {
      console.error('Error uploading farm images:', error);
      toast({
        title: "Error",
        description: "Failed to upload farm images",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (imageToRemove: FarmImage) => {
    try {
      // Extract file path from URL
      const url = new URL(imageToRemove.url);
      const pathParts = url.pathname.split('/');
      const fileName = pathParts[pathParts.length - 1];
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user && fileName) {
        const filePath = `${user.id}/${fileName}`;
        
        // Delete from storage
        const { error } = await supabase.storage
          .from('farm-logos')
          .remove([filePath]);

        if (error) throw error;
      }

      const newImages = images.filter(img => img.id !== imageToRemove.id);
      setImages(newImages);
      
      const profileIcon = newImages.find(img => img.role === 'profile_icon');
      const mainFarm = newImages.find(img => img.role === 'main_farm');
      onImagesUpdate(newImages, profileIcon?.url, mainFarm?.url);

      toast({
        title: "Success",
        description: "Farm image removed successfully!",
      });

    } catch (error) {
      console.error('Error removing farm image:', error);
      toast({
        title: "Error", 
        description: "Failed to remove farm image",
        variant: "destructive",
      });
    }
  };

  if (!isEditing && images.length === 0) {
    return null;
  }

  const groupedImages = {
    profile_icon: images.filter(img => img.role === 'profile_icon'),
    main_farm: images.filter(img => img.role === 'main_farm'),
    farm_extra: images.filter(img => img.role === 'farm_extra')
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-green-800 flex items-center">
          <Image className="mr-2" size={20} />
          Farm Images
        </CardTitle>
        <p className="text-sm text-gray-600">
          Add up to 4 images: Profile icon, main farm image for your public profile, and 2 additional farm images.
        </p>
      </CardHeader>
      <CardContent className="p-4">
        <div className="space-y-6">
          {/* Profile Icon Section */}
          <div>
            <h4 className="font-medium text-gray-800 mb-2 flex items-center">
              <User size={16} className="mr-2" />
              Profile Icon
            </h4>
            {groupedImages.profile_icon.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {groupedImages.profile_icon.map((image) => (
                  <div key={image.id} className="relative w-24 h-24">
                    <img
                      src={image.url}
                      alt="Profile icon"
                      className="w-full h-full object-cover rounded-full border-2 border-blue-200"
                    />
                    {isEditing && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full p-0"
                        onClick={() => handleRemoveImage(image)}
                      >
                        <X size={12} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-full flex items-center justify-center">
                <User size={24} className="text-gray-400" />
              </div>
            )}
          </div>

          {/* Main Farm Image Section */}
          <div>
            <h4 className="font-medium text-gray-800 mb-2 flex items-center">
              <Star size={16} className="mr-2" />
              Main Farm Image
            </h4>
            {groupedImages.main_farm.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {groupedImages.main_farm.map((image) => (
                  <div key={image.id} className="relative">
                    <div className="aspect-video relative group w-64">
                      <img
                        src={image.url}
                        alt="Main farm image"
                        className="w-full h-full object-cover rounded-lg border"
                      />
                      {isEditing && (
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => handleRemoveImage(image)}
                        >
                          <X size={12} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="aspect-video w-64 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                <Star size={32} className="text-gray-400" />
              </div>
            )}
          </div>

          {/* Additional Farm Images Section */}
          <div>
            <h4 className="font-medium text-gray-800 mb-2 flex items-center">
              <Camera size={16} className="mr-2" />
              Additional Farm Images ({groupedImages.farm_extra.length}/2)
            </h4>
            {groupedImages.farm_extra.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {groupedImages.farm_extra.map((image) => (
                  <div key={image.id} className="relative">
                    <div className="aspect-square relative group">
                      <img
                        src={image.url}
                        alt="Farm image"
                        className="w-full h-full object-cover rounded-lg border"
                      />
                      {isEditing && (
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => handleRemoveImage(image)}
                        >
                          <X size={12} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {[1, 2].map((i) => (
                  <div key={i} className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                    <Camera size={24} className="text-gray-400" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {isEditing && images.length < 4 && (
            <div className="space-y-2">
              <Input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                multiple
                className="w-full"
              />
              <Button
                variant="outline"
                size="sm"
                disabled={uploading}
                className="w-full"
                onClick={() => {
                  const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
                  if (fileInput) fileInput.click();
                }}
              >
                <Upload size={16} className="mr-2" />
                {uploading ? "Uploading..." : `Upload Images (${images.length}/4)`}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Supported formats: JPEG, PNG, WebP, GIF (max 5MB each)
                <br />
                Images will be automatically assigned roles based on upload order.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
